import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from 'react-leaflet';
import L, { LatLngTuple } from 'leaflet';
import useMapdata from 'hooks/consume_api/query/useMapdata';
import { MenuItem, FormControl, Select, InputLabel, SelectChangeEvent } from '@mui/material';
import useDemoOrInfraDataForMap from 'hooks/consume_api/query/useDemoOrInfraDataForMap';

const customIcon = new L.Icon({
    iconUrl: require('leaflet/dist/images/marker-icon.png').default,
    iconSize: [15, 31],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: require('leaflet/dist/images/marker-shadow.png').default,
    shadowSize: [41, 41]
});

type MapMarker = {
    position: [number, number];
    tooltipText: string;
};

type MapMarkersResponse = MapMarker[];

const Map: React.FC = () => {
    const position = [51.505, -0.09];
    const markers: { position: LatLngTuple; tooltipText: string }[] = [
        { position: [23.685, 90.3563], tooltipText: 'Marker 1' },
        { position: [24.685, 91.3563], tooltipText: 'Marker 2' },
        { position: [22.685, 89.3563], tooltipText: 'Marker 3' }
    ];
    const [mapData, setMapData] = useState<MapMarkersResponse>([]);
    const [selectedOption, setSelectedOption] = useState<string>('division');
    const [type, setType] = useState<string>("beneficiary");

    const [projectId, setProjectId] = useState<number>(-1);

    const handleChange = (event: SelectChangeEvent): void => {
        setSelectedOption(event.target.value);
    };

    const handleType = (event: SelectChangeEvent) => {
        setType(event.target.value);
    }
    const handleProjectChange = (event: SelectChangeEvent) => {
        setProjectId(Number(event.target.value));
    }


    const shouldFetchMapData = type === 'beneficiary' || type === 'demo';
    const { data } = useMapdata(selectedOption, type, projectId, shouldFetchMapData);

    // Only fetch infra data using the old hook
    // const { data: demoOrInfra } = useDemoOrInfraDataForMap(type === 'infra' ? type : '', selectedOption);
    const { data: demoOrInfra } = useDemoOrInfraDataForMap(type, selectedOption, projectId);

    useEffect(() => {
        if (type === 'infra' && demoOrInfra?.data) {
            try {
                const parsedData = demoOrInfra.data.map((marker: any) => {
                    const positionArray = marker.possition || marker.position;
                    return {
                        ...marker,
                        position: [parseFloat(positionArray[0]), parseFloat(positionArray[1])],
                        tooltipText: marker.tooltipText || marker.tooltip_text || ""
                    };
                });
                setMapData(parsedData);
            } catch (error) {
                console.error('Error parsing demoOrInfra data:', error);
                setMapData([]);
            }
        }
    }, [demoOrInfra, type])

    useEffect(() => {
        if (shouldFetchMapData && data?.data) {
            try {
                const parsedData = data.data.map((marker: any) => ({
                    ...marker,
                    position: [parseFloat(marker.position[0]), parseFloat(marker.position[1])],
                    tooltipText: marker.tooltipText || marker.tooltip_text || ""
                }));
                setMapData(parsedData);
            } catch (error) {
                console.error('Error parsing map data:', error);
                setMapData([]);
            }
        }
    }, [data, shouldFetchMapData, type]);
    return (
        <>
            <FormControl variant="outlined" style={{ marginTop: '20px', marginBottom: '20px', width: '200px' }}>
                <InputLabel id="project-dropdown-label">Select Project</InputLabel>
                <Select
                    labelId="project-dropdown-label"
                    id="project-dropdown"
                    value={projectId.toString()}
                    onChange={handleProjectChange}
                    label="Select Project"
                >
                    <MenuItem value={-1}>All</MenuItem>
                    <MenuItem value={1}>SACP</MenuItem>
                    <MenuItem value={2}>RAINS</MenuItem>
                </Select>
            </FormControl>


            <FormControl variant="outlined" style={{ marginTop: '20px', marginBottom: '20px', marginLeft: '20px', width: '200px' }}>
                <InputLabel id="dropdown-label">Select Location</InputLabel>
                <Select labelId="dropdown-label" id="dropdown" value={selectedOption} onChange={handleChange} label="Select Category">
                    <MenuItem value="division">Division</MenuItem>
                    <MenuItem value="district">District</MenuItem>
                    <MenuItem value="upazila">Upazila</MenuItem>
                </Select>
            </FormControl>

            <FormControl variant="outlined" style={{ marginTop: '20px', marginBottom: '20px', marginLeft: '20px', width: '300px' }}>
                <InputLabel id="dropdown-label">Select Demonstration or Infrastructure</InputLabel>
                <Select labelId="dropdown-label" id="dropdown" value={type} label="Select Category" onChange={handleType}>

                    <MenuItem value="beneficiary">Beneficiaries</MenuItem>
                    <MenuItem value="demo">Demonstration</MenuItem>
                    <MenuItem value="infra">Infrastructure</MenuItem>

                </Select>
            </FormControl>

            <MapContainer center={[23.685, 90.3563]} zoom={7} scrollWheelZoom={false} style={{ height: '80vh', width: '100%' }}>
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {mapData.map((marker, index) => {
                    const text = marker.tooltipText || "1";
                    const [title, ...rest] = text.split('\n');
                    const body = rest.join('\n');

                    const isPermanentTooltip = type === 'beneficiary' && (selectedOption === 'division');
                    let tooltipContent;

                    if (isPermanentTooltip) {
                        const amount = body.replace(/[^0-9]/g, '');
                        tooltipContent = (
                            <div style={{ padding: '2px', fontWeight: 'bold', fontSize: '12px', textAlign: 'center', color: '#333' }}>
                                {amount}
                            </div>
                        );
                    } else {
                        tooltipContent = (
                            <div style={{ minWidth: '120px', padding: '4px' }}>
                                <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '2px', color: '#333' }}>
                                    {title}
                                </div>
                                {body && (
                                    <div style={{ fontSize: '12px', color: '#666', whiteSpace: 'pre-line', lineHeight: '1.4' }}>
                                        {body}
                                    </div>
                                )}
                            </div>
                        );
                    }
                    return (
                        <Marker key={`${index}-${type}-${selectedOption}`} position={marker.position} icon={customIcon}>
                            <Tooltip permanent={isPermanentTooltip} direction="top" offset={[0, -30]} className="custom-map-tooltip">
                                {tooltipContent}
                            </Tooltip>
                        </Marker>
                    );
                })}
            </MapContainer>
        </>
    );
};

export default Map;
